/**
 * The case's checklist: what the case agent plans to establish, and what a
 * person adds, settles or takes off — one line per check, on a subject or
 * on the case, with its status, its note and how many records bear on it.
 * Every change is the person's, recorded as theirs; the agent reads the
 * list as it stands.
 */

import { useState } from 'react';

import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Group,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import {
  CHECKLIST_STATUSES,
  isSettled,
  type ChecklistItem,
  type ChecklistStatus,
} from '@/types/workbench';
import { toError } from '@/utils/errors';
import { CHECKLIST_STATUS } from './format';

interface ChecklistPanelProps {
  reportId: string;
  items: ChecklistItem[];
  subjects: { id: string; name: string }[];
  /** A person may add, change and remove lines. */
  editable: boolean;
  /** The item a link names: outlined. */
  focusedId?: string | null;
  /** After a change: the case refetches. */
  onChanged: () => void | Promise<void>;
  /** Open a check's records in the Evidence view. */
  onOpenRecords?: (itemId: string) => void;
}

const STATUS_OPTIONS = CHECKLIST_STATUSES.map((status) => ({
  value: status,
  label: CHECKLIST_STATUS[status].label,
}));

function failure(err: unknown, fallback: string): string {
  return toError(err, fallback).message;
}

function Line({
  reportId,
  item,
  editable,
  focused,
  onChanged,
  onOpenRecords,
}: Readonly<{
  reportId: string;
  item: ChecklistItem;
  editable: boolean;
  focused: boolean;
  onChanged: () => void | Promise<void>;
  onOpenRecords?: (itemId: string) => void;
}>) {
  const [busy, setBusy] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [note, setNote] = useState(item.note ?? '');
  const status = CHECKLIST_STATUS[item.status];
  const records = item.evidence.supports + item.evidence.contradicts + item.evidence.context;

  const change = async (what: Parameters<typeof vnextApiClient.changeChecklistItem>[2]) => {
    setBusy(true);
    try {
      await vnextApiClient.changeChecklistItem(reportId, item.id, what);
      await onChanged();
    } catch (err) {
      Sentry.captureException(toError(err));
      notifications.show({ color: 'red', message: failure(err, 'Not changed') });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await vnextApiClient.removeChecklistItem(reportId, item.id);
      await onChanged();
    } catch (err) {
      Sentry.captureException(toError(err));
      notifications.show({ color: 'red', message: failure(err, 'Not removed') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack
      gap={2}
      py={4}
      px={6}
      style={
        focused
          ? {
              borderRadius: 'var(--mantine-radius-sm)',
              background: 'var(--mantine-color-blue-light)',
            }
          : undefined
      }
      data-testid={`checklist-${item.id}`}
      data-focused={focused || undefined}
    >
      <Group gap={8} wrap="nowrap" align="center">
        {editable ? (
          <Select
            size="xs"
            w={132}
            aria-label={`Status of ${item.title}`}
            data={STATUS_OPTIONS}
            value={item.status}
            onChange={(value) => value && void change({ status: value as ChecklistStatus })}
            disabled={busy}
            allowDeselect={false}
            data-testid={`checklist-status-${item.id}`}
          />
        ) : (
          <Badge size="xs" variant="light" color={status.color} w={100} style={{ flex: 'none' }}>
            {status.label}
          </Badge>
        )}
        <Text
          size="sm"
          style={{ flex: 1, minWidth: 0 }}
          c={isSettled(item.status) ? 'dimmed' : undefined}
          data-testid={`checklist-title-${item.id}`}
        >
          <Text span size="xs" c="dimmed" mr={6}>
            {item.key}
          </Text>
          {item.title}
        </Text>
        {records > 0 && onOpenRecords ? (
          <Tooltip
            label={`${item.evidence.supports} supporting · ${item.evidence.contradicts} contradicting · ${item.evidence.context} context`}
            withArrow
          >
            <Anchor
              component="button"
              type="button"
              size="xs"
              onClick={() => onOpenRecords(item.id)}
              data-testid={`checklist-records-${item.id}`}
            >
              {records} record{records === 1 ? '' : 's'}
              {item.evidence.contradicts > 0 ? ` · ${item.evidence.contradicts} contradicting` : ''}
            </Anchor>
          </Tooltip>
        ) : null}
        {item.addedBy ? (
          <Badge size="xs" variant="outline" color="gray" style={{ textTransform: 'none' }}>
            added by a person
          </Badge>
        ) : null}
        {editable ? (
          <ActionIcon
            size="sm"
            variant="subtle"
            color="gray"
            aria-label={`Remove ${item.title}`}
            onClick={() => void remove()}
            disabled={busy}
            data-testid={`checklist-remove-${item.id}`}
          >
            <IconX size={14} />
          </ActionIcon>
        ) : null}
      </Group>
      {editingNote ? (
        <Group gap={6} align="flex-end" wrap="nowrap">
          <Textarea
            size="xs"
            autosize
            minRows={1}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            aria-label="Note"
            style={{ flex: 1 }}
            data-testid={`checklist-note-input-${item.id}`}
          />
          <Button
            size="compact-xs"
            loading={busy}
            onClick={() =>
              void change({ note: note.trim() || null }).then(() => setEditingNote(false))
            }
          >
            Save
          </Button>
          <Button size="compact-xs" variant="subtle" onClick={() => setEditingNote(false)}>
            Cancel
          </Button>
        </Group>
      ) : item.note || editable ? (
        <Text
          size="xs"
          c="dimmed"
          pl={editable ? 140 : 108}
          data-testid={`checklist-note-${item.id}`}
        >
          {item.note ?? ''}
          {editable ? (
            <Anchor
              component="button"
              type="button"
              size="xs"
              ml={item.note ? 6 : 0}
              onClick={() => {
                setNote(item.note ?? '');
                setEditingNote(true);
              }}
            >
              {item.note ? 'Edit the note' : 'Add a note'}
            </Anchor>
          ) : null}
        </Text>
      ) : null}
    </Stack>
  );
}

function AddLine({
  reportId,
  subjects,
  onChanged,
}: Readonly<{
  reportId: string;
  subjects: { id: string; name: string }[];
  onChanged: () => void | Promise<void>;
}>) {
  const [title, setTitle] = useState('');
  const [subjectId, setSubjectId] = useState<string | null>(subjects[0]?.id ?? null);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      return;
    }
    setBusy(true);
    try {
      await vnextApiClient.addChecklistItem(reportId, { title: trimmed, subjectId });
      setTitle('');
      await onChanged();
    } catch (err) {
      Sentry.captureException(toError(err));
      notifications.show({ color: 'red', message: failure(err, 'Not added') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Group gap={6} wrap="nowrap" align="flex-end" data-testid="checklist-add">
      <TextInput
        size="xs"
        placeholder="One more check"
        aria-label="A check to add"
        value={title}
        onChange={(e) => setTitle(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void add();
          }
        }}
        style={{ flex: 1 }}
        data-testid="checklist-add-title"
      />
      <Select
        size="xs"
        w={180}
        aria-label="On"
        data={[
          ...subjects.map((s) => ({ value: s.id, label: s.name })),
          { value: '', label: 'The case' },
        ]}
        value={subjectId ?? ''}
        onChange={(value) => setSubjectId(value || null)}
        allowDeselect={false}
        data-testid="checklist-add-subject"
      />
      <Button
        size="xs"
        variant="default"
        disabled={!title.trim()}
        loading={busy}
        onClick={() => void add()}
        data-testid="checklist-add-button"
      >
        Add
      </Button>
    </Group>
  );
}

export function ChecklistPanel({
  reportId,
  items,
  subjects,
  editable,
  focusedId = null,
  onChanged,
  onOpenRecords,
}: Readonly<ChecklistPanelProps>) {
  const names = new Map(subjects.map((s) => [s.id, s.name]));
  const groups = new Map<string, ChecklistItem[]>();
  for (const item of items) {
    const key = item.subjectId ?? 'case';
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  // The subjects in the case's order, the case's own last.
  const order = [...subjects.map((s) => s.id).filter((id) => groups.has(id)), 'case'].filter(
    (key, i, all) => groups.has(key) && all.indexOf(key) === i
  );
  const settled = items.filter((item) => isSettled(item.status)).length;

  return (
    <Stack gap="xs" data-testid="checklist">
      <Group gap={6}>
        <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
          Checklist
        </Text>
        <Text size="xs" c="dimmed" data-testid="checklist-count">
          {items.length === 0 ? 'nothing yet' : `${settled} of ${items.length} settled`}
        </Text>
      </Group>
      {items.length === 0 ? (
        <Text size="xs" c="dimmed">
          The case agent writes the checklist as it reads the request; add a check here when you
          know one it needs.
        </Text>
      ) : null}
      {order.map((key) => (
        <Stack key={key} gap={0}>
          {(groups.size > 1 || key !== 'case') && (
            <Text size="xs" fw={600} px={6}>
              {key === 'case' ? 'The case' : (names.get(key) ?? 'A subject')}
            </Text>
          )}
          {(groups.get(key) ?? [])
            .sort((a, b) => a.position - b.position)
            .map((item) => (
              <Line
                key={item.id}
                reportId={reportId}
                item={item}
                editable={editable}
                focused={item.id === focusedId}
                onChanged={onChanged}
                onOpenRecords={onOpenRecords}
              />
            ))}
        </Stack>
      ))}
      {editable ? <AddLine reportId={reportId} subjects={subjects} onChanged={onChanged} /> : null}
    </Stack>
  );
}
